import React, { useState } from 'react';

type FormStatus = 'idle' | 'loading' | 'success' | 'error';

const ContactPage: React.FC = () => {
    const [status, setStatus] = useState<FormStatus>('idle');

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setStatus('loading');
        try {
            const formData = new FormData(e.currentTarget);
            const res = await fetch('/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(formData as any).toString(),
            });
            if (res.ok) {
                setStatus('success');
                (e.target as HTMLFormElement).reset();
            } else {
                setStatus('error');
            }
        } catch {
            setStatus('error');
        }
    };

    const setDutchValidity = (e: React.FormEvent<any>) => {
        const el = e.target as HTMLInputElement;
        if (el.validity.valueMissing) el.setCustomValidity('Dit veld is verplicht.');
        else if (el.validity.typeMismatch) el.setCustomValidity('Vul een geldig e-mailadres in.');
        else el.setCustomValidity('');
    };
    const clearValidity = (e: React.ChangeEvent<any>) => {
        (e.target as HTMLInputElement).setCustomValidity('');
    };

    return (
        <div className="bg-friet-beige/30 min-h-screen">
            {/* Hero banner */}
            <div className="bg-friet-black py-20 px-4 text-center">
                <p className="font-script text-friet-gold text-2xl mb-2">Laten we kennismaken</p>
                <h1 className="font-serif text-5xl md:text-6xl text-white mb-4">
                    Neem <span className="text-friet-red">Contact</span> Op
                </h1>
                <p className="text-friet-beige/70 text-lg max-w-xl mx-auto">
                    Heb je een vraag of wil je een offerte? We staan voor je klaar.
                </p>
            </div>

            {/* Quick contact bar */}
            <div className="bg-friet-red py-4 px-4">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 text-white text-sm font-bold">
                    <a href="tel:+32478758023" className="flex items-center gap-2 hover:text-friet-yellow transition-colors">
                        <i className="fas fa-phone"></i> +32 478 75 80 23
                    </a>
                    <span className="hidden sm:block opacity-30">|</span>
                    <a href="mailto:info@frietplus.com" className="flex items-center gap-2 hover:text-friet-yellow transition-colors">
                        <i className="fas fa-envelope"></i> info@frietplus.com
                    </a>
                    <span className="hidden sm:block opacity-30">|</span>
                    <a href="https://wa.me/32478758023" className="flex items-center gap-2 hover:text-friet-yellow transition-colors">
                        <i className="fab fa-whatsapp"></i> WhatsApp
                    </a>
                </div>
            </div>

            {/* Main content */}
            <div className="max-w-6xl mx-auto px-4 py-16">
                <div className="grid md:grid-cols-2 gap-12">

                    {/* Contact info */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-3xl shadow-lg p-8 border border-friet-beige">
                            <h2 className="font-serif text-2xl text-friet-black mb-6 flex items-center gap-3">
                                <span className="w-8 h-8 bg-friet-red rounded-full flex items-center justify-center">
                                    <i className="fas fa-info text-white text-xs"></i>
                                </span>
                                Contactgegevens
                            </h2>
                            <div className="space-y-5">
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 bg-friet-beige rounded-2xl flex items-center justify-center text-friet-red text-lg flex-shrink-0">
                                        <i className="fas fa-phone"></i>
                                    </div>
                                    <div>
                                        <p className="font-bold text-friet-black">Bel Ons</p>
                                        <p className="text-sm text-gray-500 mb-1">Maandag – Zaterdag, 09:00 – 18:00</p>
                                        <a href="tel:+32478758023" className="text-friet-red font-bold hover:text-friet-black transition-colors">+32 478 75 80 23</a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 bg-friet-beige rounded-2xl flex items-center justify-center text-friet-gold text-lg flex-shrink-0">
                                        <i className="fas fa-envelope"></i>
                                    </div>
                                    <div>
                                        <p className="font-bold text-friet-black">Email Ons</p>
                                        <p className="text-sm text-gray-500 mb-1">We antwoorden binnen 2 uur</p>
                                        <a href="mailto:info@frietplus.com" className="text-friet-red font-bold hover:text-friet-black transition-colors">info@frietplus.com</a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 bg-friet-beige rounded-2xl flex items-center justify-center text-green-600 text-lg flex-shrink-0">
                                        <i className="fab fa-whatsapp"></i>
                                    </div>
                                    <div>
                                        <p className="font-bold text-friet-black">WhatsApp</p>
                                        <p className="text-sm text-gray-500 mb-1">Direct contact voor snelle vragen</p>
                                        <a href="https://wa.me/32478758023" className="text-friet-red font-bold hover:text-friet-black transition-colors">Chat nu met ons</a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 bg-friet-beige rounded-2xl flex items-center justify-center text-friet-red text-lg flex-shrink-0">
                                        <i className="fas fa-map-marker-alt"></i>
                                    </div>
                                    <div>
                                        <p className="font-bold text-friet-black">Locatie</p>
                                        <p className="text-sm text-gray-500">Dworp, Vlaams-Brabant</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-friet-black rounded-3xl p-8 text-white">
                            <h3 className="font-script text-friet-gold text-2xl mb-4">Onze Belofte</h3>
                            <ul className="space-y-3 text-friet-beige/90">
                                <li className="flex items-center gap-3"><i className="fas fa-star text-friet-gold text-sm"></i> Altijd een glimlach</li>
                                <li className="flex items-center gap-3"><i className="fas fa-star text-friet-gold text-sm"></i> Eerlijke, transparante prijzen</li>
                                <li className="flex items-center gap-3"><i className="fas fa-star text-friet-gold text-sm"></i> Passie voor perfecte frieten</li>
                            </ul>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="bg-white rounded-3xl shadow-lg p-8 border border-friet-beige">
                        <h2 className="font-serif text-2xl text-friet-black mb-6 flex items-center gap-3">
                            <span className="w-8 h-8 bg-friet-red rounded-full flex items-center justify-center">
                                <i className="fas fa-paper-plane text-white text-xs"></i>
                            </span>
                            Stuur een Bericht
                        </h2>
                        {status === 'success' && (
                            <div className="mb-5 bg-green-50 border border-green-200 text-green-800 px-5 py-4 rounded-xl font-serif text-sm flex items-start gap-3">
                                <i className="fas fa-check-circle mt-0.5 text-green-600"></i>
                                <div><strong>Bedankt!</strong> Je bericht is succesvol verzonden. We nemen zo snel mogelijk contact op.</div>
                            </div>
                        )}
                        {status === 'error' && (
                            <div className="mb-5 bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-xl font-serif text-sm flex items-start gap-3">
                                <i className="fas fa-exclamation-circle mt-0.5 text-red-500"></i>
                                <div><strong>Er ging iets mis.</strong> Probeer het opnieuw of stuur een e-mail naar <a href="mailto:info@frietplus.com" className="underline">info@frietplus.com</a>.</div>
                            </div>
                        )}
                        <form className="space-y-4" name="contact" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit}>
                            <input type="hidden" name="form-name" value="contact" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-friet-black mb-2">Voornaam <span className="text-friet-red">*</span></label>
                                    <input name="voornaam" type="text" required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-friet-beige/20 border-2 border-transparent focus:border-friet-red focus:bg-white outline-none p-3 text-base rounded-xl transition-all" placeholder="Jan" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-friet-black mb-2">Achternaam <span className="text-friet-red">*</span></label>
                                    <input name="achternaam" type="text" required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-friet-beige/20 border-2 border-transparent focus:border-friet-red focus:bg-white outline-none p-3 text-base rounded-xl transition-all" placeholder="Peeters" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-friet-black mb-2">Email Adres <span className="text-friet-red">*</span></label>
                                <input name="email" type="email" required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-friet-beige/20 border-2 border-transparent focus:border-friet-red focus:bg-white outline-none p-3 text-base rounded-xl transition-all" placeholder="jan@voorbeeld.be" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-friet-black mb-2">Telefoonnummer</label>
                                <input name="telefoon" type="tel" onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-friet-beige/20 border-2 border-transparent focus:border-friet-red focus:bg-white outline-none p-3 text-base rounded-xl transition-all" placeholder="+32 ..." />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-friet-black mb-2">Type Event</label>
                                <select name="event" className="w-full bg-friet-beige/20 border-2 border-transparent focus:border-friet-red focus:bg-white outline-none p-3 text-base rounded-xl transition-all appearance-none cursor-pointer">
                                    <option value="">-- Kies een type --</option>
                                    <option>Huwelijksfeest</option>
                                    <option>Bedrijfsevent</option>
                                    <option>Kinderfeestje</option>
                                    <option>Privéfeest / Jubileum</option>
                                    <option>Festival</option>
                                    <option>Andere</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-friet-black mb-2">Je Bericht <span className="text-friet-red">*</span></label>
                                <textarea name="bericht" rows={4} required onInvalid={setDutchValidity} onChange={clearValidity} className="w-full bg-friet-beige/20 border-2 border-transparent focus:border-friet-red focus:bg-white outline-none p-3 text-base rounded-xl transition-all" placeholder="Vertel ons meer over je plannen..."></textarea>
                            </div>
                            <button type="submit" disabled={status === 'loading'} className="w-full bg-friet-red text-white py-4 rounded-xl font-bold text-lg hover:bg-friet-black transition-all shadow-md hover:shadow-xl tracking-wide disabled:opacity-60">
                                {status === 'loading' ? 'Bezig met verzenden...' : 'Verstuur Aanvraag'}
                            </button>
                        </form>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ContactPage;
